// ============================================================
// LE BRUT D'UNE PIÈCE, DEPUIS SA GÉOMÉTRIE — une implémentation, deux appelants.
// ------------------------------------------------------------
// Ces fonctions vivaient dans `api/services/devis.service.js`. Le serveur
// recalculait le brut à l'enregistrement ; l'écran, lui, attendait la
// réponse. Philippe, 2026-10-027 (22/10) : il tape une cote manquante et
// « ne voit pas comment la faire prendre en compte » — rien ne bougeait à
// l'écran avant l'enregistrement, 2,5 s plus tard au mieux.
//
// Elles passent dans `shared/` pour que l'écran recalcule à la seconde
// où une cote de la pièce change, avec LA MÊME fonction que le serveur.
// Deux calculs du brut, c'est deux bruts (voir `brutAffiche.js`).
// ============================================================

import { variantesBrut, cotesBrut } from './brut.js';
import { finishedVolumeOf } from './geometry.js';
import { pieceAvecBrutDeConsigne } from './consigneBrut.js';

/**
 * Les cotes de la pièce FINIE, dans le vocabulaire de `cotesBrut`.
 *
 * Sur une pièce de révolution, la longueur axiale est `part.length` ;
 * `part.h` n'est qu'un repli, pour les géométries écrites avant le
 * 22 octobre 2026 par l'écran des cotes lues, qui rangeait la
 * « Profondeur » d'une pièce ronde dans `h`.
 */
export function cotesDeLaPiece(piece) {
  const part = piece?.geometry?.part;
  if (!part) return null;
  return piece.geometry?.partShape === 'cylinder'
    ? { diametre_mm: part.diameter, longueur_mm: part.length ?? part.h }
    : { longueur_mm: part.l, largeur_mm: part.w, epaisseur_mm: part.h };
}

/** Les quatre variantes de brut, recalculées depuis la géométrie. */
export function variantesDeLaPiece(piece, settings, volumeFiniCm3) {
  const part = piece?.geometry?.part;
  if (!part) return piece?.brut_variants ?? null;
  const cotes = cotesDeLaPiece(piece);
  const utilisables = Object.values(cotes).filter((v) => Number.isFinite(v) && v > 0).length;
  // Moins de deux cotes : il n'y a pas d'enveloppe à borner. On ne rend
  // pas quatre formes vides — on rend ce qu'on avait, c'est-à-dire rien.
  if (utilisables < 2) return piece.brut_variants ?? null;
  return variantesBrut(cotes, settings, volumeFiniCm3 ?? null);
}

/**
 * Pose les cotes du brut de la forme retenue — SEULEMENT là où elles
 * manquent. Une cote de brut saisie par le deviseur n'est jamais touchée.
 */
export function poserCotesDuBrut(piece) {
  const variante = piece?.brut_variants?.[piece?.raw_shape]?.cotes;
  if (!variante) return piece;
  const out = { ...piece };
  const poser = (champ, valeur) => {
    if (out[champ] == null && valeur != null) out[champ] = valeur;
  };
  poser('raw_diameter', variante.diametre_mm);
  poser('raw_length', variante.longueur_mm);
  poser('raw_l', out.raw_shape === 'barreau' ? variante.longueur_mm : null);
  poser('raw_w', variante.largeur_mm);
  poser('raw_h', variante.epaisseur_mm);
  return out;
}

/**
 * Ce que l'ÉCRAN fait quand une cote de la pièce change : variantes,
 * cotes du brut retenu, puis le brut de la consigne s'il y en a un — le
 * même enchaînement que `priceOnePiece` côté serveur.
 */
export function brutDepuisGeometrie(piece, settings = {}) {
  const volume = piece?.geometry ? finishedVolumeOf(piece.geometry) : null;
  const avecVariantes = {
    ...piece,
    brut_variants: variantesDeLaPiece({ ...piece, finished_volume: volume }, settings, volume),
  };
  const pose = poserCotesDuBrut(avecVariantes);
  return pieceAvecBrutDeConsigne(pose, cotesDeLaPiece(pose) ?? {}, cotesBrut, settings).piece;
}
